import classNames from 'classnames';
import { NodeStatus } from 'components';
import { DeviceIsSystemType } from 'features/integrations/utils/deviceComparisionUtils';
import React, { useEffect, useState } from 'react';
import { Popup } from 'react-leaflet';
import { fetchAwareImageObjectUrl } from 'utils/awareImageUtils';
import styles from '../DeviceGroupSettings.module.css';

export default function DevicePinPopup({ device, onSelect }) {
  // Guard against missing device data
  if (!device) {
    return null;
  }

  const deviceData = device.data?.[0];

  return (
    <Popup>
      <div className={styles['popup']}>
        <div className={styles['data-container']}>
          <h2>{device.static_id + ' ' + device.alias}</h2>

          <div className={styles['status']}>
            <b>Status:</b> <NodeStatus device={device} />
          </div>

          {deviceData && (deviceData.date_created || deviceData.ts) && (
            <b>{new Date(deviceData.date_created || deviceData.ts).toLocaleString()}</b>
          )}

          {DeviceIsSystemType(device, 'Flood') && <RenderAwareDeviceData device={device} />}

          {device.system_type.toLowerCase().includes('rescalert') && (
            <RenderRescAlertDeviceData device={device} />
          )}

          <button
            className={classNames('fusion-button', styles['button'])}
            onClick={() => onSelect(device.id)}
          >
            View Device
          </button>
        </div>
      </div>
    </Popup>
  );
}

function RenderRescAlertDeviceData({ device }) {
  const deviceData = device.data?.[0];

  return (
    <div>
      <b>Comments:</b> {deviceData?.comments || <i>No comments available.</i>}
    </div>
  );
}

function RenderAwareDeviceData({ device }) {
  const deviceData = device.data?.[0];
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    let isMounted = true;
    let objectUrl = null;

    const loadImage = async () => {
      if (!deviceData?.img_link_url) {
        setImageUrl(null);
        return;
      }

      objectUrl = await fetchAwareImageObjectUrl(deviceData.img_link_url);

      if (!isMounted) {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        return;
      }

      setImageUrl(objectUrl);
    };

    loadImage();

    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [deviceData?.img_link_url]);

  if (!deviceData) {
    return <div className={styles['aware-data']}>No data available</div>;
  }

  return (
    <div className={styles['aware-data']}>
      {/* Data points */}
      <div className={styles['data-points']}>
        <div>
          Actual Depth:{' '}
          {`${(deviceData.depth1 || 0) + (device?.water_level_adj || 0)} in / ${(
            ((deviceData.depth1 || 0) + (device?.water_level_adj || 0)) /
            12
          ).toFixed(3)} ft`}
        </div>
        <div>
          Depth:{' '}
          {(deviceData.depth1 || 0) + ' in/' + ((deviceData.depth1 || 0) / 12).toFixed(3) + ' ft'}
        </div>
        <div>
          Temperature:{' '}
          {(deviceData.h2oTemp || 0) +
            '°C/' +
            (((deviceData.h2oTemp || 0) * 9) / 5 + 32).toFixed(2) +
            ' °F'}
        </div>
        <div>
          Pressure:{' '}
          {(deviceData.baro || 0) +
            ' mBar/' +
            ((deviceData.baro || 0) * 0.02952998).toFixed(3) +
            ' inHg'}
        </div>
        <div>Battery: {`${deviceData.battery || 0}v`}</div>
        <div>
          Alert Indicated:{' '}
          {deviceData.ffi1 || deviceData.drInd || deviceData.depthInd ? 'True' : 'False'}
        </div>
      </div>

      {/* Images */}
      <div className={styles['img-container']}>
        {imageUrl && (
          <>
            <img src={imageUrl} alt={'Aware Camera Capture'} />
            <div>Image Timestamp: {new Date(deviceData.ts).toLocaleString()}</div>
          </>
        )}
      </div>
    </div>
  );
}
